import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import LocationSwitcher from "@/components/LocationSwitcher";
import { Button } from "@/components/ui/button";
import {
  LayoutGrid,
  LayoutDashboard,
  Building2,
  Activity as ActivityIcon,
  History as HistoryIcon,
  FileBarChart,
  Star,
  Settings as SettingsIcon,
  PencilRuler,
  LogOut,
  Truck,
  Users,
} from "lucide-react";

const NAV = [
  { to: "/board", label: "Live Board", icon: LayoutGrid, minRole: "attendant" },
  { to: "/dashboard", label: "Portfolio", icon: LayoutDashboard, minRole: "manager" },
  { to: "/accounts", label: "Monthly Accounts", icon: Building2, minRole: "manager" },
  { to: "/history", label: "History", icon: HistoryIcon, minRole: "manager" },
  { to: "/reports", label: "Reports", icon: FileBarChart, minRole: "manager" },
  { to: "/loyalty", label: "Loyalty", icon: Star, minRole: "manager" },
  { to: "/activity", label: "Activity", icon: ActivityIcon, minRole: "manager" },
  { to: "/team", label: "Team", icon: Users, minRole: "manager" },
  { to: "/layout-editor", label: "Layout Editor", icon: PencilRuler, minRole: "owner" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, minRole: "owner" },
];

const ROLE_RANK = { attendant: 1, staff: 1, manager: 2, owner: 3 };
const canSee = (role, minRole) => (ROLE_RANK[role] || 0) >= (ROLE_RANK[minRole] || 0);

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV.filter((n) => canSee(user?.role, n.minRole));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Sidebar (desktop / landscape tablet) */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-800 bg-slate-950 md:flex">
        <div className="flex items-center gap-3 px-6 py-6">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#A0148C]">
            <Truck className="h-6 w-6 text-white" />
          </div>
          <div className="leading-tight">
            <p className="font-heading text-sm font-extrabold uppercase tracking-wide text-slate-50">
              Discount Truck
            </p>
            <p className="text-xs font-semibold tracking-wide text-[#A0148C]">PARK STOPS</p>
          </div>
        </div>

        <nav className="flex-1 space-y-1 px-3 py-2">
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                  isActive
                    ? "bg-slate-800 text-[#A0148C] border-r-2 border-[#A0148C]"
                    : "text-slate-400 hover:bg-slate-900 hover:text-slate-200"
                }`
              }
            >
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-800 p-4">
          <div className="mb-3 px-2">
            <p className="truncate text-sm font-semibold text-slate-200">{user?.name}</p>
            <p className="text-xs uppercase tracking-wider text-slate-500">{user?.role}</p>
          </div>
          <Button
            data-testid="logout-button"
            variant="secondary"
            onClick={handleLogout}
            className="w-full justify-start gap-2 bg-slate-800 hover:bg-slate-700 text-slate-200"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="pb-24 md:ml-64 md:pb-0">
        {/* Global persistent top bar with the location switcher */}
        <div className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-slate-800 bg-slate-950/95 px-4 py-3 backdrop-blur md:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#A0148C] md:hidden">
              <Truck className="h-4 w-4 text-white" />
            </div>
            <LocationSwitcher />
          </div>
          <Button
            data-testid="logout-button-mobile"
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-slate-300 md:hidden"
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
        <Outlet />
      </main>

      {/* Bottom nav (mobile portrait) */}
      <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-slate-800 bg-slate-950 md:hidden">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`bottomnav-${to.slice(1)}`}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-1 py-3 text-[11px] font-medium ${
                isActive ? "text-[#A0148C]" : "text-slate-400"
              }`
            }
          >
            <Icon className="h-5 w-5" />
            {label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
